/**
 * Wohin die App führt — als Modell, nicht als Markup.
 *
 * Die Kopfzeile listete acht Ziele nebeneinander. Auf dem Schreibtisch geht
 * das; auf einem Telefon stehen acht Textmarken in einer Zeile, die keine
 * 44 Pixel breit sind. Eine Tab-Leiste trägt vier, mehr verträgt kein Daumen —
 * der Rest gehört hinter „Mehr". Welche vier das sind, ist eine Entscheidung
 * über den Bestand und keine über den Bildschirm: Liste, Karte und Statistik
 * sind das Logbuch, Check-in ist der Grund, das Telefon am Bootssteg
 * überhaupt hervorzuholen. Stammdatenpflege, Verorten, Abgleich und Laden
 * sind Arbeit am Bestand und finden selten am Steg statt.
 *
 * Das Modul kennt kein DOM. Es beantwortet drei Fragen: Wo bin ich, wie heisst
 * das hier, und wohin führt der Zurück-Pfeil.
 */

/**
 * Die Ziele in der Reihenfolge, in der sie erscheinen.
 *
 * `haupt` heisst: steht in der Tab-Leiste. Der fünfte Platz dort gehört immer
 * „Mehr", deshalb sind es genau vier.
 */
export const ZIELE = [
  { pfad: '/', titel: 'Liste', symbol: 'liste', haupt: true },
  { pfad: '/karte', titel: 'Karte', symbol: 'karte', haupt: true },
  { pfad: '/statistik', titel: 'Statistik', symbol: 'statistik', haupt: true },
  { pfad: '/checkin', titel: 'Check-in', symbol: 'checkin', haupt: true },
  { pfad: '/stammdaten', titel: 'Stammdaten', symbol: 'stammdaten' },
  { pfad: '/verorten', titel: 'Verorten', symbol: 'verorten' },
  { pfad: '/sync', titel: 'Sync', symbol: 'sync' },
  { pfad: '/import', titel: 'Laden', symbol: 'laden' },
];

export const HAUPTZIELE = ZIELE.filter((z) => z.haupt);
export const WEITERE_ZIELE = ZIELE.filter((z) => !z.haupt);

/**
 * Unterseiten: kein eigenes Ziel, sondern eine Ebene tiefer.
 *
 * `eltern` ist, wohin der Zurück-Pfeil führt — beim Bearbeiten also zum
 * Tauchgang und nicht zur Liste, sonst verlöre man beim Zurückgehen die
 * Stelle, an der man gerade war.
 */
const UNTERSEITEN = [
  { muster: /^\/tauchgang\/(.+)$/, titel: 'Tauchgang', eltern: () => '/' },
  { muster: /^\/neu$/, titel: 'Neuer Tauchgang', eltern: () => '/', formular: true },
  {
    muster: /^\/bearbeiten\/(.+)$/,
    titel: 'Bearbeiten',
    eltern: (treffer) => `/tauchgang/${treffer[1]}`,
    formular: true,
  },
];

/** Den Pfad aus einer Adresse schälen — ohne Raute, ohne Suchteil. */
export function pfadAus(hash) {
  return String(hash ?? '').replace(/^#/, '').split('?')[0] || '/';
}

/**
 * Welches Ziel ist gemeint?
 *
 * `/stammdaten/sites` gehört zu `/stammdaten`; deshalb gewinnt der längste
 * passende Pfad und nicht der erste.
 */
export function zielFuer(pfad) {
  const p = pfadAus(pfad);
  let treffer = null;
  for (const ziel of ZIELE) {
    if (ziel.pfad === '/') continue;
    if (p === ziel.pfad || p.startsWith(`${ziel.pfad}/`)) {
      if (!treffer || ziel.pfad.length > treffer.pfad.length) treffer = ziel;
    }
  }
  if (treffer) return treffer;
  return ZIELE[0];
}

function unterseite(pfad) {
  const p = pfadAus(pfad);
  for (const seite of UNTERSEITEN) {
    const treffer = p.match(seite.muster);
    if (treffer) return { seite, treffer };
  }
  return null;
}

/**
 * Wie tief liegt die Seite?
 *
 * 0 sind die Ziele der Tab-Leiste, 1 alles, was von dort aus geöffnet wird.
 * Daran hängen der Zurück-Pfeil, die Richtung des Übergangs und die Frage, ob
 * die Tab-Leiste stehen bleibt.
 */
export function ebene(pfad) {
  return unterseite(pfad) ? 1 : 0;
}

/** Der Titel in der Kopfzeile. Auf der Liste steht der Name der App. */
export function titelFuer(pfad) {
  const unten = unterseite(pfad);
  if (unten) return unten.seite.titel;
  const ziel = zielFuer(pfad);
  return ziel.pfad === '/' ? 'Tauchlogbuch' : ziel.titel;
}

/**
 * Ist die Seite eine Arbeit mit eigenem Abschluss?
 *
 * Ein Tauchgang ist eine Seite tiefer, ein Formular ist etwas anderes: Es hat
 * einen Fuss mit „Speichern" und einen Zustand, der verloren geht. Die
 * Tab-Leiste weicht dort, damit nicht ein Fehltipp zwei Zentimeter unter dem
 * Speichern-Knopf die halbe Eingabe wegwirft.
 */
export function istFormular(pfad) {
  return Boolean(unterseite(pfad)?.seite.formular);
}

/** Wohin der Zurück-Pfeil führt — `null`, wenn es keinen gibt. */
export function elternPfad(pfad) {
  const unten = unterseite(pfad);
  return unten ? unten.seite.eltern(unten.treffer) : null;
}

/**
 * In welche Richtung wird gewechselt?
 *
 * Vorwärts schiebt sich die neue Seite von rechts herein, rückwärts geht sie
 * nach rechts hinaus — das ist die einzige Bewegung, die auf einem Telefon
 * ohne Erklärung verstanden wird. Zwischen zwei Zielen derselben Ebene wird
 * nicht geschoben, sondern geblendet: Ein Wechsel von der Karte zur Statistik
 * ist kein Hinein und kein Heraus.
 */
export function richtung(vonPfad, nachPfad) {
  if (vonPfad === null || vonPfad === undefined) return 'quer';
  const a = ebene(vonPfad);
  const b = ebene(nachPfad);
  if (b > a) return 'vor';
  if (b < a) return 'zurueck';
  return 'quer';
}
